/**
 * Multi-Quote Mileage Simulator Page
 * Advanced simulator with multiple quote options and automatic ranking
 */

import { Plane, RotateCcw, ArrowLeftRight, AlertTriangle, Trophy } from 'lucide-react';
import { useMultiQuoteSimulator } from '../hooks/useMultiQuoteSimulator';
import { CurrencyInput } from '../components/CurrencyInput';
import { QuoteList } from '../components/QuoteList';
import { BestComboCard } from '../components/BestComboCard';
import { ComboComparison } from '../components/ComboComparison';
import { ProfitCalculator } from '../components/ProfitCalculator';
import { getComboStats } from '../utils/combos';

export default function MultiQuoteSimulator() {
  const {
    quotation,
    desiredChargeAmount,
    ranking,
    selectedCombo,
    profitAnalysis,
    validation,
    setAirlinePrice,
    setRoundTrip,
    addOutboundQuote,
    updateOutboundQuote,
    removeOutboundQuote,
    addReturnQuote,
    updateReturnQuote,
    removeReturnQuote,
    setDesiredCharge,
    selectCombo,
    reset,
  } = useMultiQuoteSimulator();

  const stats = ranking ? getComboStats(ranking) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#B05D41]/10 via-transparent to-transparent" />

      <div className="relative">
        {/* Header */}
        <header className="border-b border-zinc-800/50 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                  <img src="/ugoo-branco.svg" alt="Travel Legacy" className="w-12 h-12" />
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl sm:text-3xl font-bold text-zinc-100">
                      Simulador de Milhas
                      <span className="text-[#B05D41]"> Travel Legacy</span>
                    </h1>
                  </div>
                  <p className="text-sm sm:text-base text-zinc-400 mt-1">
                    Compare múltiplas cotações e encontre a melhor combinação
                  </p>
                </div>
              </div>
              <button
                onClick={reset}
                className="p-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-zinc-100 transition-all"
                title="Resetar simulador"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Airline Price Section */}
          <section className="bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-lg bg-blue-500/10 text-blue-500">
                <Plane className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-zinc-100">
                  Preço de Referência
                </h2>
                <p className="text-sm text-zinc-400">
                  Quanto a companhia aérea está cobrando?
                </p>
              </div>
            </div>

            <CurrencyInput
              label="Valor total da Companhia"
              value={quotation.airlinePrice}
              onChange={setAirlinePrice}
              placeholder="0,00"
              required
              className="max-w-md"
            />
          </section>

          {/* Quote Management Section */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-[#B05D41]/10 text-[#B05D41]">
                  <ArrowLeftRight className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-zinc-100">
                    Cotações de Milhas
                  </h2>
                  <p className="text-sm text-zinc-400">
                    Adicione todas as opções que você encontrou
                  </p>
                </div>
              </div>

              {/* Round Trip Toggle */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <span className="text-sm text-zinc-300 group-hover:text-zinc-100 transition-colors">
                  Ida e Volta
                </span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={quotation.isRoundTrip}
                    onChange={(e) => setRoundTrip(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-zinc-700 peer-focus:ring-2 peer-focus:ring-[#B05D41]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B05D41]"></div>
                </div>
              </label>
            </div>

            {/* Quote Lists */}
            <div className="grid lg:grid-cols-2 gap-6">
              <QuoteList
                title="Opções de Ida"
                quotes={quotation.outboundQuotes}
                onAdd={addOutboundQuote}
                onUpdate={updateOutboundQuote}
                onRemove={removeOutboundQuote}
                icon="outbound"
              />
              {quotation.isRoundTrip && (
                <QuoteList
                  title="Opções de Volta"
                  quotes={quotation.returnQuotes}
                  onAdd={addReturnQuote}
                  onUpdate={updateReturnQuote}
                  onRemove={removeReturnQuote}
                  icon="return"
                />
              )}
            </div>

            {/* Validation Errors */}
            {!validation.isValid && validation.errors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-red-400 mb-2">
                  Atenção:
                </h3>
                <ul className="space-y-1">
                  {validation.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-400 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Stats Banner */}
          {ranking && stats && (
            <div className="bg-gradient-to-r from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-purple-400" />
                  <p className="text-sm text-zinc-300">
                    <span className="font-semibold text-purple-400">{stats.totalCombos}</span>
                    {' '}combinações geradas •
                    <span className="font-semibold text-emerald-400"> {stats.viableCombos}</span>
                    {' '}viáveis •
                    <span className="font-semibold text-red-400"> {stats.nonViableCombos}</span>
                    {' '}inviáveis
                  </p>
                </div>
                {ranking.bestCombo && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <span className="text-xs text-zinc-400">Máxima economia:</span>
                    <span className="text-sm font-bold text-emerald-400">
                      {ranking.bestCombo.savings.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Best Combo Highlight */}
          {ranking?.bestCombo && ranking.hasViableOption && (
            <section>
              <BestComboCard
                combo={ranking.bestCombo}
                airlinePrice={quotation.airlinePrice}
              />
            </section>
          )}

          {/* No Viable Options Warning */}
          {ranking && !ranking.hasViableOption && ranking.combos.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6 text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                Nenhuma Opção Viável Encontrada
              </h3>
              <p className="text-sm text-zinc-400">
                Todas as combinações têm custo maior ou igual ao preço da companhia aérea.
                Considere adicionar cotações com valores mais baixos.
              </p>
            </div>
          )}

          {/* Combo Comparison */}
          {ranking && ranking.combos.length > 1 && (
            <section>
              <ComboComparison
                ranking={ranking}
                onSelectCombo={(comboId) => {
                  const combo = ranking.combos.find(c => c.id === comboId);
                  selectCombo(combo || null);
                }}
                selectedComboId={selectedCombo?.id}
              />
            </section>
          )}

          {/* Profit Calculator */}
          {selectedCombo && (
            <section>
              <ProfitCalculator
                profitAnalysis={profitAnalysis}
                desiredChargeAmount={desiredChargeAmount}
                onDesiredChargeChange={setDesiredCharge}
                airlinePrice={quotation.airlinePrice}
              />
            </section>
          )}

          {/* Footer */}
          <footer className="text-center py-8 border-t border-zinc-800/50">
            <p className="text-sm text-zinc-400">
              Simulador de Milhas • Travel Legacy © {new Date().getFullYear()}
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              Ferramenta avançada para comparação de cotações e análise de lucro
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}
